import { Box, TextField, InputAdornment, IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useTranslation } from "react-i18next";
import MultiSelectDropdown from "../CustomDropdowns/MultiSelectDropdown";
import MobileFilters from "./MobileFilters";
import ClearFiltersButton from "./ClearFiltersButton";

const CommunitySuppliersHeaderFilters = (props) => {
  const {
    searchValue,
    setSearchValue,
    originalSuppliers,
    setSuppliers,
    clearFilters,
    selectedCategories,
    setSelectedCategories,
    selectedLocations,
    setSelectedLocations,
    categories = [],
    // Which tree the categories come from — the supplier index browses a
    // different catalog per tab, and each has its own labels.
    categoriesNamespace = "categories",
    zones = [],
    selectedMemberRanges = [],
    setSelectedMemberRanges,
    rangesDropDownList = [],
    hasFilters = false,
    showMobileFilters = true,
  } = props;

  const { t } = useTranslation([
    "header-filters",
    "categories",
    "products_categories",
    "restaurants_categories",
    "districts",
  ]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        gap: "8px",
        flexWrap: "wrap",
      }}
      display="flex"
      mb={"15px"}
    >
      <Box
        sx={{
          width: {
            xl: "260px",
            lg: "260px",
            md: "250px",
            sm: "240px",
            xs: "100%",
          },
        }}
      >
        <TextField
          value={searchValue}
          onChange={(e) => {
            const value = e.target.value;
            setSearchValue(value);
            if (value.trim() === "" && originalSuppliers) {
              setSuppliers(originalSuppliers);
            }
          }}
          variant="outlined"
          placeholder={t("search")}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchValue && (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => {
                    setSearchValue("");
                    if (originalSuppliers) {
                      setSuppliers(originalSuppliers);
                    }
                  }}
                  size="small"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: "39px",
              fontWeight: 400,
              fontSize: "15px",
              color: "rgba(62, 63, 76, 0.7)",
              borderRadius: "8px",
              backgroundColor: "#ffffff",
              "& fieldset": {
                borderWidth: "1px",
                borderColor: "#EAEAEA",
              },
              "&:hover fieldset": {
                borderWidth: "1px",
                borderColor: "#3e3f4c",
              },
              "&.Mui-focused fieldset": {
                borderWidth: "1px",
                borderColor: "#3e3f4c",
              },
            },
          }}
        />
      </Box>

      <Box
        display="flex"
        alignItems="center"
        sx={{
          display: {
            sm: "flex",
            xs: showMobileFilters ? "none" : "flex",
          },
          flexWrap: "wrap",
          gap: { xs: "8px", sm: "16px" },
        }}
      >
        {categories.length > 0 ? (
          <Box>
            <MultiSelectDropdown
              value={selectedCategories}
              onChange={setSelectedCategories}
              grouped={true}
              options={categories.map((group) => ({
                groupLabel: t(group.name, { ns: categoriesNamespace }),
                options: group.sub_categories.map((item) => ({
                  value: item.id,
                  label: t(item.name, { ns: categoriesNamespace }),
                  image: item.image_url,
                })),
              }))}
              placeholder={t("filters.category")}
              buttonIconPath="/icons/grid-view-icon.svg"
              sx={{
                button: {
                  minWidth: "200px",
                  backgroundColor: "#fff",
                  "&:hover": {
                    borderColor: "rgba(36, 139, 112, 1)",
                    backgroundColor: "#fff",
                  },
                },
                menuPaper: {
                  width: "auto",
                },
              }}
            />
          </Box>
        ) : null}

        <Box>
          <MultiSelectDropdown
            value={selectedLocations}
            onChange={setSelectedLocations}
            options={zones.map((zone) => ({
              value: zone.id,
              label: t(zone.name, { ns: "districts" }),
            }))}
            placeholder={t("filters.location")}
            buttonIconPath="/icons/grid-view-icon.svg"
            sx={{
              button: {
                minWidth: "200px",
                backgroundColor: "#fff",
                "&:hover": {
                  borderColor: "rgba(36, 139, 112, 1)",
                  backgroundColor: "#fff",
                },
              },
              menuPaper: {
                width: "auto",
              },
            }}
          />
        </Box>

        {rangesDropDownList.length > 0 ? (
          <Box>
            <MultiSelectDropdown
              value={selectedMemberRanges}
              onChange={setSelectedMemberRanges}
              options={rangesDropDownList.map((range) => ({
                value: range.value,
                label: range.key,
              }))}
              placeholder={t("filters.member_range")}
              buttonIconPath="/people_icon.svg"
              sx={{
                button: {
                  minWidth: "200px",
                  backgroundColor: "#fff",
                  "&:hover": {
                    borderColor: "rgba(36, 139, 112, 1)",
                    backgroundColor: "#fff",
                  },
                },
                menuPaper: {
                  width: "auto",
                },
              }}
            />
          </Box>
        ) : null}

        <ClearFiltersButton
          hasFilters={hasFilters}
          clearFilters={clearFilters}
        />
      </Box>

      {showMobileFilters ? (
        <MobileFilters
          {...{
            selectedCategories,
            selectedLocations,
            categories,
            zones,
            setSelectedCategories,
            setSelectedLocations,
            selectedMemberRanges,
            setSelectedMemberRanges,
            memberRanges: rangesDropDownList,
            clearFilters,
            hasFilters,
          }}
        />
      ) : null}
    </Box>
  );
};

export default CommunitySuppliersHeaderFilters;
